import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTab } from "../../../store/tabSlice";
import "./styles.scss";
import { setNavbarParams, setText } from "../../../store/navbar";
import {
  getStudentEndPoints,
  MustIncludedSidebarItems,
} from "../../../constants/sidebar";
import { setLinks, showLinks } from "../../../store/sidebar";
import {
  getUserRole,
  getUserInformation,
  getUserPermission,
} from "../../../store/userRole";
import {
  getSingalClassroomData,
  getPracticesOfSubject,
} from "../../../utils/classrooms";
import Loader from "../../../components/Loader";
import ActivityTabsHeader from "../../../components/StudentDashboard/ActivityTabsHeader/ActivityTabsHeader";
import { clearAll } from "../../../store/practiceSet";
import { extractModulesInMentionedOrder } from "../../../utils/validations";
import TopicItem from "./components/TopicItem";
import Sidebar from "./components/Sidebar";
import MobileView from "./components/MobileView";
import AttemptDialog from "./AttemptDialog";

const StudentPractice = ({ isMobileView, hasValidSubscription }) => {
  // Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: classroomId } = useParams();

  // Redux state
  const activeTab = useSelector((state) => state.tab.activeTab);
  const roleOfTheUser = useSelector(getUserRole);
  const rolePermissions = useSelector(getUserPermission);
  const userInformation = useSelector(getUserInformation);

  // Local state
  const [isLoading, setIsLoading] = useState(true);
  const [classroomName, setClassroomName] = useState("");
  const [classroomLoader, setClassroomLoader] = useState(true);
  const [tabsList, setTabsList] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [sectionsList, setSectionsList] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedMobileTopic, setSelectedMobileTopic] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [selectedPractice, setSelectedPractice] = useState({});
  const [topicsList, setTopicsList] = useState([]);
  const [createdBy, setCreatedBy] = useState("");

  const queryParams = new URLSearchParams(window.location.search);
  const isNotifyConfig = queryParams.get("notificationConfig");
  const notifySub = queryParams.get("subj");
  const notifySec = queryParams.get("sec");
  const notifyTopic = queryParams.get("topic");
  const notifyExam = queryParams.get("examId");
  const [focusedExam, setFocusExam] = useState("");

  const isFirstRef = useRef(false);

  useEffect(() => {
    if (
      isNotifyConfig &&
      isNotifyConfig === "true" &&
      isFirstRef.current === false &&
      sectionsList?.length
    ) {
      isFirstRef.current = true;
      setActiveTab(notifySub);
      setSelectedSections([notifySec]);
      if (isMobileView) {
        setSelectedMobileTopic([notifyTopic]);
      } else {
        setSelectedTopic(notifyTopic);
        scrollToTopic(notifyTopic);
      }
      setFocusExam(notifyExam);
      setTimeout(() => {
        setFocusExam("");
      }, [3000]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotifyConfig, sectionsList]);

  const getClassroomDetails = async () => {
    try {
      setClassroomLoader(true);
      const response = await getSingalClassroomData(classroomId);
      if (response?.data?.classRoom) {
        if (response?.data?.classRoom?.name) {
          setClassroomName(response?.data?.classRoom?.name);
          setModules(response?.data?.classRoom?.modulesIncluded);
          setCreatedBy(response?.data?.classRoom?.createdBy);
          const subjects = response?.data?.classRoom?.course?.subjects
            .filter((subject) => !subject?.isHidden)
            .map((subject) => {
              return {
                id: subject?._id,
                label: subject?.name,
              };
            });
          if (response?.data?.classRoom?.course?._id) {
            setCourseId(response?.data?.classRoom?.course?._id);
          }
          if (subjects?.length > 0) {
            const isCurrentTabValid = subjects.some(s => s.id === activeTab);
            if (!activeTab || !isCurrentTabValid) {
              dispatch(setActiveTab(subjects[0].id));
            }
          }
          setTabsList(subjects || []);
        }
      }
    } catch (error) {
      console.error("Error fetching classrooms:", error.message);
    }
    setClassroomLoader(false);
  };

  useEffect(() => {
    getClassroomDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab) {
      dispatch(setActiveTab(activeTab));
    }
  }, [activeTab, dispatch]);

  useEffect(() => {
    const heading = "student-dashboard";
    dispatch(setText(heading));
    dispatch(setNavbarParams(`Practice | ${classroomName}`));
    const defaultItems = [
      ...getStudentEndPoints(
        classroomId,
        roleOfTheUser,
        userInformation?._id === createdBy,
        rolePermissions
      ),
    ];
    const sidebarItems = defaultItems.filter(
      (item) =>
        modules.includes(item?.value) ||
        MustIncludedSidebarItems.includes(item?.value)
    );
    const indOfItem = sidebarItems?.findIndex(
      (item) => item.value === "Practice"
    );
    if (indOfItem !== -1) {
      sidebarItems[indOfItem].selected = true;
    }
    dispatch(setLinks(extractModulesInMentionedOrder(sidebarItems, modules)));
    dispatch(showLinks());
  }, [
    classroomId,
    classroomName,
    createdBy,
    dispatch,
    modules,
    roleOfTheUser,
    rolePermissions,
    userInformation?._id,
  ]);

  const getPracticesListOfSubject = async () => {
    try {
      setIsLoading(true);
      const response = await getPracticesOfSubject(
        classroomId,
        courseId,
        activeTab
      );
      if (response.ok && response?.data?.sections) {
        // First, filter topics within each section to only include non-hidden topics with practices
        const sectionsWithPractices = response?.data?.sections.map(
          (section) => {
            // Make sure topics exists before trying to filter it
            const topics = section?.topics || [];
            const filteredTopics = topics
              .filter((topic) => !topic.isHidden)
              .filter(
                (topic) => topic?.practices && topic?.practices?.length > 0
              );

            return { ...section, topics: filteredTopics };
          }
        );

        // Then filter out sections that don't have any valid topics remaining
        const filteredSections = sectionsWithPractices.filter(
          (section) => section?.topics && section?.topics?.length > 0
        );

        setSectionsList(filteredSections);

        // Only proceed with setting selected sections if there are any sections left
        if (filteredSections.length > 0) {
          const firstSection = filteredSections[0];
          setSelectedSections([firstSection?._id]);

          if (firstSection?.topics && firstSection?.topics.length > 0) {
            setSelectedTopic(firstSection?.topics[0]?._id);
            setSelectedMobileTopic([firstSection?.topics[0]?._id]);
          } else {
            // Reset these in case we previously had a selection
            setSelectedTopic("");
            setSelectedMobileTopic([]);
          }

          let topicArray = [];
          filteredSections.forEach((section) => {
            if (section?.topics && section?.topics.length > 0) {
              topicArray = [...topicArray, ...section?.topics];
            }
          });
          setTopicsList(topicArray);
        } else {
          // Reset all selections if there are no valid sections
          setSectionsList([]);
          setSelectedSections([]);
          setSelectedTopic("");
          setSelectedMobileTopic([]);
          setTopicsList([]);
        }
      }
    } catch (error) {
      console.error("Error fetching classrooms:", error.message);
    }
    setIsLoading(false);
  };

  const scrollToTopic = useCallback((topicId) => {
    const topicElement = document.getElementById(topicId);
    if (topicElement) {
      topicElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "" && courseId) {
      getPracticesListOfSubject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, courseId]);

  const onAttemptHandler = () => {
    dispatch(clearAll());
    const basePath = roleOfTheUser === "Student" ? "/student" : "";
    const attemptType = selectedPractice?.type === "writing"
      ? "writingAttempt"
      : selectedPractice?.type === "speaking"
        ? "speakingAttempt"
        : "practiceAttempt";
    navigate(
      `${basePath}/myClassrooms/${classroomId}/${activeTab}/${selectedTopicId}/practice/${attemptType}/${
        selectedPractice?._id
      }?activeTab=${activeTab}`
    );
  };

  const onViewAttempts = () => {
    navigate(
      `${
        roleOfTheUser === "Student" ? "/student" : ""
      }/myClassrooms/${classroomId}/${activeTab}/practice/attempt-results/${
        selectedPractice?._id
      }`
    );
  };

  const onViewAllAttempts = () => {
    navigate(
      `${
        roleOfTheUser === "Student" ? "/student" : ""
      }/myClassrooms/${classroomId}/${activeTab}/practice/all-attempts`
    );
  };

  const handlePracticeStart = (topicId, practice) => {
    setSelectedTopicId(topicId);
    setSelectedPractice(practice);
    setOpen(true);
  };

  if (isLoading || classroomLoader) {
    return <Loader />;
  }

  return (
    <div>
      {open && (
        <AttemptDialog
          open={open}
          setOpen={setOpen}
          practice={selectedPractice}
          onAttempt={onAttemptHandler}
          onViewAttempts={onViewAttempts}
        />
      )}
      <div style={{ position: "sticky", top: "-1px", zIndex: 6 }}>
        <ActivityTabsHeader
          isMobileView={isMobileView}
          tabsList={tabsList}
          activeTab={activeTab}
          onTabChange={(newTab) => dispatch(setActiveTab(newTab))}
        />
      </div>
      <div className="row m-0">
        <Sidebar
          sectionsList={sectionsList}
          selectedSections={selectedSections}
          setSelectedSections={setSelectedSections}
          selectedTopic={selectedTopic}
          setSelectedTopic={setSelectedTopic}
          selectedMobileTopic={selectedMobileTopic}
          setSelectedMobileTopic={setSelectedMobileTopic}
          scrollToTopic={scrollToTopic}
          roleOfTheUser={roleOfTheUser}
          classroomId={classroomId}
          activeTab={activeTab}
          hasValidSubscription={hasValidSubscription}
          onViewAllAttempts={onViewAllAttempts}
          isLoading={isLoading}
          classroomLoader={classroomLoader}
          isMobileView={false}
        />

        <div className="d-none d-lg-block col-8" style={{ marginTop: "15px" }}>
          {topicsList.length
            ? topicsList.map((topic, index) => (
                <TopicItem
                  key={`topic-index-${index}`}
                  topic={topic}
                  roleOfTheUser={roleOfTheUser}
                  classroomId={classroomId}
                  activeTab={activeTab}
                  hasValidSubscription={hasValidSubscription}
                  onPracticeStart={handlePracticeStart}
                  selected={topic._id === selectedTopic}
                  focusedExam={focusedExam}
                />
              ))
            : ""}
        </div>

        <MobileView
          sectionsList={sectionsList}
          selectedSections={selectedSections}
          setSelectedSections={setSelectedSections}
          selectedMobileTopic={selectedMobileTopic}
          setSelectedMobileTopic={setSelectedMobileTopic}
          roleOfTheUser={roleOfTheUser}
          classroomId={classroomId}
          activeTab={activeTab}
          hasValidSubscription={hasValidSubscription}
          onViewAllAttempts={onViewAllAttempts}
          isLoading={isLoading}
          classroomLoader={classroomLoader}
          handlePracticeStart={handlePracticeStart}
        />
      </div>
    </div>
  );
};

export default memo(StudentPractice);
